import { useState, useEffect, useRef } from 'react';

const LOGO_URL = 'https://storage.readdy-site.link/project_files/a1db2c25-97e2-4ceb-a4ec-c06941f029b5/a3ac0cdf-0792-4180-b72e-0df4bd1c7c5e_FMA-CREW-LOGO.png?v=b37410eb2aa41c47106263f8d4f50ddf';
const FORM_URL = 'https://readdy.ai/api/form/d71j6pbk1jkj467r5ha0';
const CONTACT_TITLE = '문의전화/이메일';
const CONTACT_PHONE_1 = '470-756-9942';
const CONTACT_PHONE_1_HREF = 'tel:4707569942';
const CONTACT_PHONE_2 = '919-706-9422';
const CONTACT_PHONE_2_HREF = 'tel:9197069422';
const CONTACT_EMAIL = 'office@fmacrew.com';
const WEBSITE_URL = 'https://www.fmacrew.com';
const OFFICE_ADDRESS_LINE1 = '6500 McDonough Dr. Suite C15';
const OFFICE_ADDRESS_LINE2 = 'Norcross, GA 30093';

const IMG_LIVING = 'https://public.readdy.ai/ai/img_res/edited_fbf5490214fddf4092ec669e874fd51f_40fb9fb0.jpg';
const IMG_KITCHEN = 'https://public.readdy.ai/ai/img_res/edited_7c2498358a0f8610f7f15f206aa9f01f_fe7f0143.jpg';
const IMG_BEDROOM = 'https://public.readdy.ai/ai/img_res/edited_b4419633f67ca49a4ec2422b9e3a7e02_a95c4ab5.jpg';

const IMG_HOTSPOT_BG = 'https://public.readdy.ai/ai/img_res/edited_429b8b838370f7444114037f3b5f9c9e_64356c3f.jpg';
const IMG_HS_STORAGE = 'https://public.readdy.ai/ai/img_res/60547ac78e6d437cd6ecf0f6adde8617.jpg';
const IMG_HS_SMART = 'https://readdy.ai/api/search-image?query=smart%20home%20kitchen%20modern%20appliances%20touch%20panel%20contemporary%20interior%20efficient%20systems%20sleek%20clean%20design%20technology&width=220&height=140&seq=fma-hs-s2&orientation=landscape';
const IMG_HS_WARM = 'https://readdy.ai/api/search-image?query=warm%20cozy%20interior%20pendant%20lamp%20golden%20warm%20glow%20living%20room%20evening%20ambiance%20soft%20illumination%20elegant%20mood%20lighting%20design&width=220&height=140&seq=fma-hs-s3&orientation=landscape';

const HOTSPOT_DATA = [
  {
    id: 'storage',
    x: 43, y: 64,
    title: '깔끔한 수납',
    desc: '공간을 낭비하지 않는 스마트 수납 설계',
    icon: 'ri-archive-drawer-line',
    img: IMG_HS_STORAGE,
    tipAbove: true,
    tipLeft: false,
  },
  {
    id: 'smart',
    x: 64, y: 26,
    title: '스마트한 설비',
    desc: '최신 기술로 더 편리한 생활 시스템',
    icon: 'ri-settings-3-line',
    img: IMG_HS_SMART,
    tipAbove: false,
    tipLeft: true,
  },
  {
    id: 'warm',
    x: 22, y: 43,
    title: '따뜻한 분위기',
    desc: '감성을 더하는 섬세한 마감과 조명 설계',
    icon: 'ri-fire-line',
    img: IMG_HS_WARM,
    tipAbove: true,
    tipLeft: false,
  },
];

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

export default function AdPage01() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [hoveredHotspot, setHoveredHotspot] = useState<string | null>(null);

  const s1 = useInView();
  const s2 = useInView();
  const s3 = useInView();
  const sAtlanta = useInView();
  const s7 = useInView();
  const s8 = useInView();

  useEffect(() => {
    window.scrollTo(0, 0);
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (message.length > 500) return;
    setFormStatus('loading');
    try {
      const form = e.currentTarget;
      const raw = new FormData(form);
      const data = new URLSearchParams();
      raw.forEach((value, key) => data.append(key, value.toString()));
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setFormStatus(res.ok ? 'success' : 'error');
    } catch {
      setFormStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5] font-sans">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-white/95 backdrop-blur-sm border-b border-stone-100' : 'bg-transparent'}`}>
        <div className="mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <a href={WEBSITE_URL} target="_blank" rel="noopener noreferrer" className="h-9 flex items-center cursor-pointer">
            <img src={LOGO_URL} alt="FMA Crew" className={`h-full w-auto object-contain transition-all duration-300 ${scrolled ? '' : 'brightness-0 invert'}`} />
          </a>
          <div className="flex items-center gap-2">
            <a href={CONTACT_PHONE_1_HREF} className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${scrolled ? 'bg-[#3D2B1F] text-white hover:bg-[#5C3D2A]' : 'bg-white/15 border border-white/30 text-white hover:bg-white/25'}`}>
              <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-phone-fill text-xs" /></div>
              {CONTACT_PHONE_1}
            </a>
            <a href={CONTACT_PHONE_2_HREF} className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${scrolled ? 'bg-[#3D2B1F] text-white hover:bg-[#5C3D2A]' : 'bg-white/15 border border-white/30 text-white hover:bg-white/25'}`}>
              <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-phone-fill text-xs" /></div>
              {CONTACT_PHONE_2}
            </a>
          </div>
        </div>
      </header>

      {/* ─── PHOTO HERO ─── */}
      <section className="relative w-full pt-0">
        <div className="grid grid-cols-12 h-[92vh] min-h-[640px]">
          <div className="col-span-7 relative overflow-hidden">
            <img src={IMG_LIVING} alt="감성적인 거실 인테리어" className="w-full h-full object-cover object-center scale-105 animate-[slowzoom_12s_ease-in-out_infinite_alternate]" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/20 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
          </div>
          <div className="col-span-5 flex flex-col">
            <div className="flex-1 relative overflow-hidden">
              <img src={IMG_KITCHEN} alt="스마트 주방 인테리어" className="w-full h-full object-cover object-center scale-105 animate-[slowzoom_15s_ease-in-out_2s_infinite_alternate]" />
              <div className="absolute inset-0 bg-black/20" />
            </div>
            <div className="h-1 bg-[#FAF8F5]" />
            <div className="flex-1 relative overflow-hidden">
              <img src={IMG_BEDROOM} alt="편안한 침실 인테리어" className="w-full h-full object-cover object-center scale-105 animate-[slowzoom_18s_ease-in-out_4s_infinite_alternate]" />
              <div className="absolute inset-0 bg-black/15" />
            </div>
          </div>
        </div>
        <div className="absolute inset-0 flex flex-col justify-end pb-16 px-8 md:px-16 pointer-events-none">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/25 backdrop-blur-sm px-3 py-1.5 rounded-full mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-300 animate-pulse" />
              <span className="text-white/90 text-xs tracking-[0.2em] uppercase font-medium">FMA Crew · Interior Remodeling</span>
            </div>
            <h1 className="text-white text-4xl md:text-6xl font-light leading-[1.15] mb-4 tracking-tight">
              당신의 공간에<br />
              <em className="not-italic font-semibold text-amber-200">감성을 더하다</em>
            </h1>
            <p className="text-white/75 text-sm md:text-base leading-relaxed max-w-md">편리함과 따뜻함이 공존하는 공간, FMA가 만들어드립니다</p>
          </div>
        </div>
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 pointer-events-none animate-bounce">
          <div className="w-px h-8 bg-white/50" />
          <i className="ri-arrow-down-line text-white/60 text-sm" />
        </div>
      </section>

      {/* ─── SECTION 1 ─── */}
      <section className="py-24 md:py-32 px-6 md:px-16 bg-[#FAF8F5]">
        <div ref={s1.ref} className={`max-w-4xl mx-auto transition-all duration-1000 ${s1.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="flex items-center gap-3 mb-8">
            <div className="w-8 h-px bg-amber-700/40" />
            <span className="text-amber-800/70 text-xs tracking-[0.25em] uppercase font-medium">Space &amp; Life</span>
          </div>
          <h2 className="text-[#2C1E15] text-3xl md:text-5xl font-light leading-[1.4] mb-8 tracking-tight">
            집은 단순한 공간이 아니라,<br />
            <span className="font-semibold">당신의 하루가 시작되고<br />끝나는 이야기입니다.</span>
          </h2>
          <div className="w-12 h-0.5 bg-amber-600/50 mb-10" />
          <p className="text-[#2C1E15] text-base md:text-lg leading-[1.9] font-light">
            바쁜 일상 속에서도 편리함은 기본, 감성은 선택이 아닌 필수. 우리는 최신 스마트 시스템과 동선을 고려한 설계로 당신의 생활을 더 쉽고, 더 따뜻하게 만듭니다.
          </p>
          <div className="mt-14 p-8 md:p-10 bg-[#3D2B1F] rounded-2xl relative overflow-hidden">
            <div className="absolute top-4 left-6 text-amber-200/10 text-[120px] font-serif leading-none select-none">&ldquo;</div>
            <p className="relative text-amber-50/90 text-lg md:text-2xl font-light leading-[1.7] italic">
              지금, 당신의 공간에 <strong className="font-semibold text-amber-300 not-italic">&lsquo;편리함 이상의 감성&rsquo;</strong>을<br className="hidden md:block" /> 더해보세요.
            </p>
          </div>
        </div>
      </section>

      {/* ─── DIVIDER IMAGE ─── */}
      <div className="w-full h-[360px] md:h-[480px] relative overflow-hidden">
        <img src={IMG_KITCHEN} alt="스마트 인테리어" className="w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center px-6">
            <p className="text-white/80 text-sm tracking-[0.3em] uppercase mb-3">Our Promise</p>
            <p className="text-white text-2xl md:text-4xl font-light leading-[1.5]">
              당신의 생활 패턴을 읽고,<br />
              <span className="font-semibold">가장 효율적이면서도 감각적인 공간</span>을 만듭니다
            </p>
          </div>
        </div>
      </div>

      {/* ─── SECTION 2 · Daily Comfort ─── */}
      <section className="py-24 md:py-32 px-6 md:px-16 bg-[#FAF8F5]">
        <div ref={s2.ref} className={`max-w-5xl mx-auto transition-all duration-1000 delay-200 ${s2.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-12 md:gap-16 items-center">
            {/* Left: Text */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-7">
                <div className="w-8 h-px bg-amber-700/40" />
                <span className="text-amber-800/70 text-xs tracking-[0.25em] uppercase font-medium">Daily Comfort</span>
              </div>
              <h2 className="text-[#2C1E15] text-3xl md:text-4xl font-light leading-[1.45] mb-8">
                아침에 눈을 뜨는 순간부터<br />
                <span className="font-semibold">편안함이 자연스럽게<br />스며드는 공간</span>
              </h2>
              <p className="text-[#5A3E30] text-base leading-[1.9] font-light mb-6">
                우리는 단순히 리모델링을 하지 않습니다. 당신의 생활 패턴을 읽고, 가장 효율적이면서도 감각적인 공간을 만듭니다.
              </p>
              <p className="text-[#5A3E30] text-base leading-[1.9] font-light">
                깔끔한 수납, 스마트한 설비, 그리고 따뜻한 분위기까지. 나의 하루가 달라지는 작은 변화를 만들기 위해 FMA는 오늘도 고민합니다.
              </p>
              <p className="text-stone-400 text-xs mt-6">
                <i className="ri-cursor-line mr-1" />
                핀 위에 마우스를 올리면 세부 내용을 확인할 수 있어요
              </p>
            </div>

            {/* Right: Interactive Hotspot */}
            <div className="md:col-span-3">
              <div className="relative w-full rounded-2xl overflow-hidden" style={{ paddingBottom: '62%' }}>
                <img
                  src={IMG_HOTSPOT_BG}
                  alt="FMA 인테리어 공간 상세"
                  className="absolute inset-0 w-full h-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-black/15" />

                {HOTSPOT_DATA.map((hs) => (
                  <div
                    key={hs.id}
                    className="absolute"
                    style={{ left: `${hs.x}%`, top: `${hs.y}%`, transform: 'translate(-50%, -50%)' }}
                    onMouseEnter={() => setHoveredHotspot(hs.id)}
                    onMouseLeave={() => setHoveredHotspot(null)}
                  >
                    <button
                      type="button"
                      className="relative w-9 h-9 flex items-center justify-center cursor-pointer"
                      aria-label={hs.title}
                    >
                      <span className="absolute inset-0 rounded-full bg-amber-400/40 animate-ping" />
                      <span className="relative w-9 h-9 flex items-center justify-center bg-amber-400 rounded-full border-2 border-white">
                        <i className="ri-add-line text-white text-base" />
                      </span>
                    </button>

                    {hoveredHotspot === hs.id && (
                      <div
                        className="absolute z-50 w-64 bg-white rounded-2xl overflow-hidden pointer-events-none"
                        style={{
                          ...(hs.tipAbove
                            ? { bottom: 'calc(100% + 14px)' }
                            : { top: 'calc(100% + 14px)' }),
                          ...(hs.tipLeft
                            ? { right: '-4px' }
                            : { left: '-4px' }),
                          boxShadow: '0 8px 32px rgba(60,30,10,0.18)',
                        }}
                      >
                        <div
                          className="absolute w-3 h-3 bg-white rotate-45"
                          style={{
                            ...(hs.tipAbove
                              ? { bottom: '-6px' }
                              : { top: '-6px' }),
                            ...(hs.tipLeft
                              ? { right: '20px' }
                              : { left: '20px' }),
                          }}
                        />
                        <div className="p-4">
                          <div className="flex items-center gap-2 mb-1.5">
                            <div className="w-6 h-6 flex items-center justify-center bg-amber-50 text-amber-700 rounded-lg shrink-0">
                              <i className={`${hs.icon} text-xs`} />
                            </div>
                            <span className="text-[#2C1E15] font-bold text-sm">{hs.title}</span>
                          </div>
                          <p className="text-stone-500 text-xs leading-relaxed pl-8">{hs.desc}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── DIVIDER IMAGE 2 ─── */}
      <div className="w-full h-[400px] md:h-[520px] relative overflow-hidden">
        <img src={IMG_BEDROOM} alt="따뜻한 침실 인테리어" className="w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="absolute bottom-10 left-0 right-0 px-8 md:px-16">
          <div className="max-w-4xl mx-auto">
            <p className="text-white/60 text-xs tracking-[0.3em] uppercase mb-3">The Feeling We Create</p>
            <p className="text-white text-2xl md:text-3xl font-light leading-[1.6]">
              &ldquo;아, 잘 돌아왔다&rdquo;<br />
              <span className="font-semibold text-amber-200">그 감정을 만드는 것이 우리의 일입니다.</span>
            </p>
          </div>
        </div>
      </div>

      {/* ─── SECTION 3 ─── */}
      <section className="py-24 md:py-32 px-6 md:px-16 bg-[#FAF8F5]">
        <div ref={s3.ref} className={`max-w-4xl mx-auto transition-all duration-1000 delay-200 ${s3.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-3 mb-7">
              <div className="w-8 h-px bg-amber-700/40" />
              <span className="text-amber-800/70 text-xs tracking-[0.25em] uppercase font-medium">Your Space, Redesigned</span>
              <div className="w-8 h-px bg-amber-700/40" />
            </div>
            <h2 className="text-[#2C1E15] text-3xl md:text-5xl font-light leading-[1.4] mb-6">
              당신이 집에 들어오는 순간,<br />
              <span className="font-semibold">&ldquo;아, 잘 돌아왔다&rdquo;라는<br />생각이 들게 하고 싶습니다.</span>
            </h2>
            <p className="text-[#5A3E30] text-base md:text-lg leading-[1.9] font-light max-w-2xl mx-auto">
              최신 기술로 더 편리하게, 세심한 디자인으로 더 아름답게. 우리는 공간을 고치는 것이 아니라 당신의 일상을 다시 설계합니다.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            {[
              { icon: 'ri-cpu-line', title: '기능은 더 똑똑하게', desc: '스마트홈 시스템, 동선 최적화, 에너지 효율까지 고려한 현대적 설계' },
              { icon: 'ri-palette-line', title: '분위기는 더 따뜻하게', desc: '색감, 조명, 마감재 하나하나 섬세하게 골라 공간의 온도를 높입니다' },
              { icon: 'ri-user-heart-line', title: '가장 나다운 공간으로', desc: '당신의 라이프스타일과 취향을 반영한 완전히 맞춤화된 인테리어' },
            ].map((item) => (
              <div key={item.title} className="text-center p-7 bg-white border border-stone-100 rounded-2xl hover:border-amber-200 hover:bg-amber-50/30 transition-all duration-300 group">
                <div className="w-12 h-12 flex items-center justify-center bg-amber-50 text-amber-700 rounded-2xl mx-auto mb-4 group-hover:bg-amber-100 transition-colors">
                  <i className={`${item.icon} text-xl`} />
                </div>
                <h3 className="text-[#2C1E15] font-semibold text-base mb-3">{item.title}</h3>
                <p className="text-stone-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="relative overflow-hidden bg-[#3D2B1F] rounded-2xl p-10 md:p-14 text-center">
            <div className="absolute inset-0 opacity-10">
              <img src={IMG_LIVING} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="relative">
              <p className="text-amber-200/80 text-xs tracking-[0.3em] uppercase mb-5">지금 시작하세요</p>
              <h3 className="text-white text-2xl md:text-4xl font-light leading-[1.5] mb-4">
                당신의 집을,<br />
                <strong className="font-semibold text-amber-200">가장 &lsquo;나다운 공간&rsquo;으로</strong> 바꿔보세요.
              </h3>
              <p className="text-amber-50/60 text-sm mb-8 leading-relaxed max-w-lg mx-auto">
                FMA는 당신의 이야기를 듣고, 공간으로 답합니다. 무료 상담부터 시작하세요.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a href={CONTACT_PHONE_1_HREF} className="flex items-center justify-center gap-2 bg-amber-400 text-[#2C1E15] px-8 py-4 rounded-full text-sm font-bold whitespace-nowrap cursor-pointer hover:bg-amber-300 transition-colors">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-phone-fill" /></div>
                  {CONTACT_PHONE_1}
                </a>
                <a href={CONTACT_PHONE_2_HREF} className="flex items-center justify-center gap-2 bg-amber-400 text-[#2C1E15] px-8 py-4 rounded-full text-sm font-bold whitespace-nowrap cursor-pointer hover:bg-amber-300 transition-colors">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-phone-fill" /></div>
                  {CONTACT_PHONE_2}
                </a>
                <button type="button" onClick={() => document.getElementById('inquiry-section')?.scrollIntoView({ behavior: 'smooth' })} className="flex items-center justify-center gap-2 bg-white/10 border border-white/20 text-white px-8 py-4 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-white/20 transition-colors">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-mail-send-line" /></div>
                  온라인 문의하기
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── ATLANTA SERVICE AREA ─── */}
      <section className="py-20 md:py-24 px-6 md:px-16 bg-white border-t border-stone-100">
        <div ref={sAtlanta.ref} className={`max-w-5xl mx-auto transition-all duration-1000 ${sAtlanta.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-start">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-px bg-amber-700/40" />
                <span className="text-amber-800/70 text-xs tracking-[0.25em] uppercase font-medium">Service Area</span>
              </div>
              <h2 className="text-[#2C1E15] text-3xl md:text-4xl font-light leading-[1.45] mb-6">
                아틀랜타와<br />
                <span className="font-semibold">그 너머의 공간까지,<br />FMA가 함께합니다</span>
              </h2>
              <p className="text-[#5A3E30] text-base leading-[1.9] font-light mb-5">
                아틀랜타를 중심으로 광역 생활권 전반에 서비스합니다.<br />어디든 FMA가 직접 찾아갑니다.
              </p>
              <div className="bg-[#FAF8F5] border border-stone-100 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-5 h-5 flex items-center justify-center text-amber-700">
                    <i className="ri-map-pin-2-fill text-base" />
                  </div>
                  <span className="text-[#2C1E15] font-semibold text-sm">Areas We Serve</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {['Atlanta','Marietta','Alpharetta','Duluth','Johns Creek','Smyrna','Decatur','Sandy Springs','Roswell','Peachtree City','Norcross','Lawrenceville'].map((city) => (
                    <div key={city} className="flex items-center gap-2 py-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                      <span className="text-[#3D2B1F] text-sm">{city}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-stone-200 flex items-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center text-amber-700">
                    <i className="ri-information-line text-xs" />
                  </div>
                  <p className="text-stone-500 text-xs">위 지역 외 상담 후 방문 가능</p>
                </div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden border border-stone-100 h-[420px] md:h-[480px]">
              <iframe title="FMA Crew Service Area - Greater Atlanta" src="https://maps.google.com/maps?q=Atlanta,Georgia,USA&z=9&output=embed" width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            </div>
          </div>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section className="py-20 md:py-28 px-6 md:px-16 bg-white border-t border-stone-100">
        <div ref={s7.ref} className={`max-w-3xl mx-auto transition-all duration-1000 ${s7.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="flex items-center gap-3 mb-5">
            <div className="w-8 h-px bg-amber-700/40" />
            <span className="text-amber-800/70 text-xs tracking-[0.25em] uppercase font-medium">FAQ</span>
          </div>
          <h2 className="text-[#2C1E15] text-3xl md:text-4xl font-light leading-[1.4] mb-10">
            리모델링 전에<br />
            <span className="font-semibold">꼭 확인하세요</span>
          </h2>
          <div className="flex flex-col gap-3">
            {[
              { q: '주방이나 욕실 리모델링은 보통 얼마나 걸리나요?', a: '일반적인 주방 리모델링은 6~12주, 욕실은 3~6주 소요됩니다. 착공 전 상세한 공정표를 제공해 각 단계를 명확히 파악할 수 있습니다.' },
              { q: '허가 취득과 검사 일정도 대신 처리해 주나요?', a: '네, 필요한 모든 허가를 직접 취득하고 검사 일정도 저희가 관리합니다. 시청이나 군청에 따로 연락하실 필요 없습니다.' },
              { q: '리모델링 작업에 하자 보증이 있나요?', a: '모든 시공에 1년 하자 보증을 제공합니다. 보증 기간 내 문제가 발생하면 무상으로 처리해 드립니다.' },
              { q: '영업 중에도 상업 공간 공사가 가능한가요?', a: '네, 비즈니스 운영에 지장이 없도록 야간·주말·단계적 작업 방식으로 진행합니다. 업무 중단을 최소화하는 것이 저희의 원칙입니다.' },
            ].map((faq, i) => (
              <div key={i} className="bg-[#FAF8F5] border border-stone-100 rounded-2xl overflow-hidden">
                <button type="button" onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer hover:bg-amber-50/40 transition-colors duration-200">
                  <span className="text-[#2C1E15] font-semibold text-sm">{faq.q}</span>
                  <div className={`w-5 h-5 flex items-center justify-center text-amber-700 shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`}>
                    <i className="ri-arrow-down-s-line text-base" />
                  </div>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5">
                    <div className="h-px bg-stone-100 mb-4" />
                    <p className="text-stone-500 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CLIENT STORIES ─── */}
      <section className="py-20 md:py-28 px-6 md:px-16 bg-[#FAF8F5] border-t border-stone-100">
        <div ref={s8.ref} className={`max-w-4xl mx-auto transition-all duration-1000 ${s8.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="flex items-center gap-3 mb-5">
            <div className="w-8 h-px bg-amber-700/40" />
            <span className="text-amber-800/70 text-xs tracking-[0.25em] uppercase font-medium">Client Stories</span>
          </div>
          <h2 className="text-[#2C1E15] text-3xl md:text-4xl font-light leading-[1.4] mb-10">
            고객이 직접 말하는<br />
            <span className="font-semibold">FMA의 공간 이야기</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { name: 'Michael K.', role: '주택 소유주 · Alpharetta, GA', text: '주방과 욕실 전체 리모델링을 맡겼는데, 결과물이 기대 이상이었습니다. 캐비닛 색상부터 조명 배치까지 세밀하게 신경 써주셔서 집이 완전히 새로운 공간으로 탄생했습니다. 공사 기간 동안 소통도 매우 원활했고, 약속한 일정보다 빨리 마무리해 주셨어요. 매일 아침 새롭게 꾸며진 주방을 볼 때마다 투자한 보람을 느낍니다.', service: 'Kitchen & Bath', initials: 'MK' },
              { name: 'Jennifer L.', role: '신혼부부 · Norcross, GA', text: '이사 후 거실과 침실 전체를 리노베이션했습니다. FMA 팀이 제 취향을 잘 반영해 따뜻하고 모던한 분위기로 공간을 완성해 주었어요. 바닥재 교체부터 페인트 마감까지 꼼꼼한 디테일 덕분에 집에 들어올 때마다 기분이 좋아집니다. 지인들도 모두 완성도에 감탄했고, 벌써 세 가족에게 추천했습니다.', service: 'Interior Renovation', initials: 'JL' },
            ].map((testimonial, i) => (
              <div key={i} className="bg-white border border-stone-100 rounded-2xl p-8 flex flex-col gap-5 hover:border-amber-200 transition-colors duration-300">
                <div className="flex gap-1">
                  {[1,2,3,4,5].map((s) => (
                    <div key={s} className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-star-fill text-amber-400 text-xs" />
                    </div>
                  ))}
                </div>
                <p className="text-[#3D2B1F] text-sm leading-[1.9] font-light italic flex-1">&ldquo;{testimonial.text}&rdquo;</p>
                <div className="flex items-center justify-between pt-4 border-t border-stone-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#3D2B1F] text-amber-200 rounded-full text-xs font-bold shrink-0">{testimonial.initials}</div>
                    <div>
                      <p className="text-[#2C1E15] font-semibold text-sm">{testimonial.name}</p>
                      <p className="text-stone-400 text-xs">{testimonial.role}</p>
                    </div>
                  </div>
                  <span className="px-3 py-1 bg-amber-50 text-amber-700 text-xs font-medium rounded-full border border-amber-100">{testimonial.service}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── INQUIRY FORM ─── */}
      <section id="inquiry-section" className="py-20 md:py-28 px-6 md:px-16 bg-white border-t border-stone-100">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-3 mb-5">
              <div className="w-8 h-px bg-amber-700/40" />
              <span className="text-amber-800/70 text-xs tracking-[0.25em] uppercase font-medium">무료 상담 문의</span>
              <div className="w-8 h-px bg-amber-700/40" />
            </div>
            <h2 className="text-[#2C1E15] text-2xl md:text-3xl font-semibold mb-3">지금 바로 문의하세요</h2>
            <p className="text-stone-500 text-sm leading-relaxed mb-5">24시간 이내에 담당자가 직접 연락드립니다. 비용 없이, 조건 없이.</p>
            <div className="inline-flex items-center gap-3 bg-amber-50 border border-amber-100 rounded-xl px-5 py-3">
              <div className="w-8 h-8 flex items-center justify-center bg-amber-600 text-white rounded-full shrink-0">
                <i className="ri-phone-line text-sm" />
              </div>
              <div className="text-left">
                <p className="text-[#2C1E15] font-semibold text-sm">{CONTACT_TITLE}</p>
                <p className="text-amber-700 text-xs">{CONTACT_PHONE_1} · {CONTACT_PHONE_2}</p>
                <p className="text-amber-700 text-xs">{CONTACT_EMAIL}</p>
              </div>
            </div>
          </div>
          {formStatus === 'success' ? (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-12 text-center">
              <div className="w-14 h-14 flex items-center justify-center bg-amber-400 text-white rounded-full mx-auto mb-4">
                <i className="ri-check-line text-2xl" />
              </div>
              <h3 className="text-[#2C1E15] font-bold text-lg mb-2">문의가 접수되었습니다!</h3>
              <p className="text-stone-600 text-sm">24시간 이내에 담당자가 연락드리겠습니다.</p>
            </div>
          ) : (
            <form data-readdy-form id="ad01-inquiry-form" onSubmit={handleSubmit} className="bg-[#FAF8F5] border border-stone-100 rounded-2xl p-8 md:p-10">
              <input type="hidden" name="to_1" value="office@fmacrew.com" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-[#3D2B1F] text-xs font-semibold mb-1.5 tracking-wide">성함 <span className="text-amber-600">*</span></label>
                  <input type="text" name="name" required placeholder="홍길동" className="w-full bg-white border border-stone-200 rounded-xl px-4 py-3 text-sm text-slate-800 placeholder-stone-400 focus:outline-none focus:border-amber-400 transition-colors" />
                </div>
                <div>
                  <label className="block text-[#3D2B1F] text-xs font-semibold mb-1.5 tracking-wide">이메일 <span className="text-amber-600">*</span></label>
                  <input type="email" name="email" required placeholder="example@email.com" className="w-full bg-white border border-stone-200 rounded-xl px-4 py-3 text-sm text-slate-800 placeholder-stone-400 focus:outline-none focus:border-amber-400 transition-colors" />
                </div>
              </div>
              <div className="mb-6">
                <label className="block text-[#3D2B1F] text-xs font-semibold mb-1.5 tracking-wide">연락처</label>
                <input type="tel" name="phone" placeholder="(000) 000-0000" className="w-full bg-white border border-stone-200 rounded-xl px-4 py-3 text-sm text-slate-800 placeholder-stone-400 focus:outline-none focus:border-amber-400 transition-colors" />
              </div>
              <div className="mb-6">
                <label className="block text-[#3D2B1F] text-xs font-semibold mb-1.5 tracking-wide">문의 내용 <span className="text-amber-600">*</span></label>
                <textarea name="message" required maxLength={500} rows={4} placeholder="리모델링을 원하시는 공간이나 궁금한 점을 자유롭게 적어주세요..." value={message} onChange={(e) => setMessage(e.target.value)} className="w-full bg-white border border-stone-200 rounded-xl px-4 py-3 text-sm text-slate-800 placeholder-stone-400 focus:outline-none focus:border-amber-400 transition-colors resize-none" />
                <p className="text-xs text-stone-400 text-right mt-1">{message.length}/500</p>
              </div>
              {formStatus === 'error' && <p className="text-red-500 text-xs mb-3">제출 중 오류가 발생했습니다. 전화로 직접 문의해 주세요.</p>}
              <button type="submit" disabled={formStatus === 'loading'} className="w-full bg-[#3D2B1F] text-white py-3.5 rounded-xl text-sm font-bold whitespace-nowrap cursor-pointer hover:bg-[#5C3D2A] transition-colors disabled:opacity-60">
                {formStatus === 'loading' ? '전송 중...' : '무료 상담 신청하기'}
              </button>
              <p className="text-center text-stone-400 text-xs mt-4">
                직접 연락:{' '}
                <a href={CONTACT_PHONE_1_HREF} className="text-amber-700 font-medium hover:underline cursor-pointer">{CONTACT_PHONE_1}</a>
                {' · '}
                <a href={CONTACT_PHONE_2_HREF} className="text-amber-700 font-medium hover:underline cursor-pointer">{CONTACT_PHONE_2}</a>
                {' · '}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-amber-700 font-medium hover:underline cursor-pointer">{CONTACT_EMAIL}</a>
              </p>
            </form>
          )}
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="bg-[#2C1E15] px-6 md:px-12 py-12">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 pb-10 border-b border-white/10">
            <div>
              <a href={WEBSITE_URL} target="_blank" rel="noopener noreferrer" className="inline-block mb-4 cursor-pointer">
                <img src={LOGO_URL} alt="FMA Crew" className="h-8 w-auto object-contain brightness-0 invert opacity-80" />
              </a>
              <p className="text-amber-50/50 text-xs leading-relaxed">Facility Maintenance of America<br />Interior Remodeling Specialists</p>
            </div>
            <div>
              <p className="text-amber-200/70 text-xs font-semibold tracking-[0.15em] uppercase mb-4">담당자 연락처</p>
              <div className="flex flex-col gap-2.5">
                <a href={CONTACT_PHONE_1_HREF} className="flex items-center gap-2.5 group cursor-pointer">
                  <div className="w-4 h-4 flex items-center justify-center text-amber-500"><i className="ri-phone-line text-xs" /></div>
                  <span className="text-amber-50/70 text-xs group-hover:text-amber-200 transition-colors">{CONTACT_PHONE_1}</span>
                </a>
                <a href={CONTACT_PHONE_2_HREF} className="flex items-center gap-2.5 group cursor-pointer">
                  <div className="w-4 h-4 flex items-center justify-center text-amber-500"><i className="ri-phone-line text-xs" /></div>
                  <span className="text-amber-50/70 text-xs group-hover:text-amber-200 transition-colors">{CONTACT_PHONE_2}</span>
                </a>
                <a href={`mailto:${CONTACT_EMAIL}`} className="flex items-center gap-2.5 group cursor-pointer">
                  <div className="w-4 h-4 flex items-center justify-center text-amber-500"><i className="ri-mail-line text-xs" /></div>
                  <span className="text-amber-50/70 text-xs group-hover:text-amber-200 transition-colors">{CONTACT_EMAIL}</span>
                </a>
              </div>
            </div>
            <div>
              <p className="text-amber-200/70 text-xs font-semibold tracking-[0.15em] uppercase mb-4">사무실 / 웹사이트</p>
              <div className="flex flex-col gap-2.5">
                <div className="flex items-start gap-2.5">
                  <div className="w-4 h-4 flex items-center justify-center text-amber-500 mt-0.5 shrink-0"><i className="ri-map-pin-2-line text-xs" /></div>
                  <span className="text-amber-50/70 text-xs leading-relaxed">{OFFICE_ADDRESS_LINE1}<br />{OFFICE_ADDRESS_LINE2}</span>
                </div>
                <a href={WEBSITE_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 group cursor-pointer">
                  <div className="w-4 h-4 flex items-center justify-center text-amber-500"><i className="ri-global-line text-xs" /></div>
                  <span className="text-amber-50/70 text-xs group-hover:text-amber-200 transition-colors">www.fmacrew.com</span>
                </a>
              </div>
            </div>
          </div>
          <div className="pt-8 flex flex-col md:flex-row items-center justify-between gap-3">
            <p className="text-amber-50/30 text-xs">© 2026 FMA Crew — Facility Maintenance of America. All rights reserved.</p>
            <p className="text-amber-50/30 text-xs">Serving Atlanta, GA &amp; Surrounding Areas</p>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes slowzoom {
          from { transform: scale(1.0); }
          to { transform: scale(1.08); }
        }
      `}</style>
    </div>
  );
}
